import { useAppSelector } from 'redux/hooks';
import moment from 'moment';
import { exists } from 'lib/types';
import {
  disableNonPublishingDays,
  getDeadlineString,
  isAllowedPublicationDay,
  publishingDayEnumValuesFromDeadlines
} from 'lib/utils/deadlines';
import { DataWithNoticeType, getNoticeTypeFromNoticeData } from 'lib/helpers';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import { useEffect, useState } from 'react';
import { ColumnButton } from 'lib/components/ColumnButton';
import { OrganizationModel } from 'lib/model/objects/organizationModel';
import classnames from 'classnames';
import { selectValidNoticeTypes } from '../placeScroll/placeScrollSelectors';

type PublicationScheduleCalendarProps = {
  publisherOrganization: OrganizationModel;
};

export default function PublicationScheduleCalendar({
  publisherOrganization
}: PublicationScheduleCalendarProps) {
  const validNoticeTypes =
    useAppSelector(state =>
      selectValidNoticeTypes(state, publisherOrganization)
    ) || [];
  const [selectedNoticeType, setSelectedNoticeType] = useState(
    validNoticeTypes.length ? String(validNoticeTypes[0].value) : ''
  );
  const noticeType = getNoticeTypeFromNoticeData(
    { noticeType: Number(selectedNoticeType) },
    publisherOrganization
  );
  const [selectedMonthStart, setSelectedMonthStart] =
    useState<moment.Moment | null>(null);
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth()
  );

  useEffect(() => {
    setSelectedNoticeType(
      validNoticeTypes.length ? String(validNoticeTypes[0].value) : ''
    );
  }, [publisherOrganization?.id]);

  useEffect(() => {
    const startOfSelectedMonth = moment()
      .tz(publisherOrganization.modelData.iana_timezone)
      .set('month', selectedMonth)
      .startOf('month');

    setSelectedMonthStart(startOfSelectedMonth);

    const startDate = startOfSelectedMonth.clone().startOf('week');
    const endDate = moment()
      .tz(publisherOrganization.modelData.iana_timezone)
      .set('month', selectedMonth)
      .endOf('month')
      .endOf('week');
    setStartDate(startDate);
    setEndDate(endDate);
  }, [publisherOrganization?.id, selectedMonth]);

  if (
    !publisherOrganization ||
    !exists(publisherOrganization) ||
    !startDate ||
    !endDate
  ) {
    return null;
  }

  const deadlines = publisherOrganization.modelData.deadlines || [];
  const deadlineOverrides =
    publisherOrganization.modelData.deadlineOverrides || {};

  const publicationDayEnumValues =
    publishingDayEnumValuesFromDeadlines(deadlines);
  console.log('publicationDayEnumValues', publicationDayEnumValues);
  const daysList = getDaysList(startDate, endDate);

  return (
    <div className="mb-8">
      <div className="md:flex justify-between items-end p-3">
        <div>
          <h1 className="font-bold text-lg">
            {publisherOrganization.modelData.name} – Public Notice Publication
            Schedule and Deadlines
          </h1>
          <h2 className="font-bold text-3xl mb-3">
            {selectedMonthStart?.format('MMMM Y')}
          </h2>
          <ColumnSelect
            labelText="Select a notice type"
            placeholder="Select a notice type"
            id="selectNoticeType"
            options={validNoticeTypes.map(validNoticeType => ({
              value: String(validNoticeType.value),
              label: validNoticeType.label
            }))}
            value={selectedNoticeType}
            allowUndefined
            onChange={setSelectedNoticeType}
          />
        </div>
        <div className="pt-3 space-x-3">
          <ColumnButton
            type="button"
            buttonText="Previous month"
            onClick={() => setSelectedMonth(month => month - 1)}
          />
          <ColumnButton
            type="button"
            buttonText="Today"
            onClick={() => {
              const today = moment().tz(
                publisherOrganization.modelData.iana_timezone
              );
              setSelectedMonth(today.month());
            }}
          />
          <ColumnButton
            type="button"
            buttonText="Next month"
            onClick={() => setSelectedMonth(month => month + 1)}
          />
        </div>
      </div>
      <div className="md:grid grid-cols-7 gap-4 p-3">
        <div className="hidden md:block p-3 bg-white">Sunday</div>
        <div className="hidden md:block p-3 bg-white">Monday</div>
        <div className="hidden md:block p-3 bg-white">Tuesday</div>
        <div className="hidden md:block p-3 bg-white">Wednesday</div>
        <div className="hidden md:block p-3 bg-white">Thursday</div>
        <div className="hidden md:block p-3 bg-white">Friday</div>
        <div className="hidden md:block p-3 bg-white">Saturday</div>
        {daysList.map(day => {
          const isDayEnabledOnDefaultSchedule = !disableNonPublishingDays(
            day.toDate(),
            publicationDayEnumValues,
            deadlineOverrides,
            publisherOrganization.modelData.iana_timezone
          );
          const isDayEnabledForNoticeType = isAllowedPublicationDay(
            day.toDate(),
            noticeType
          );
          const isDayEnabled =
            isDayEnabledOnDefaultSchedule && isDayEnabledForNoticeType;

          const linerDeadline = getDeadlineString(
            deadlines,
            deadlineOverrides,
            day.toDate(),
            { noticeType: noticeType?.value } as DataWithNoticeType,
            publisherOrganization
          );
          const displayDeadline = getDeadlineString(
            deadlines,
            deadlineOverrides,
            day.toDate(),
            {
              noticeType: noticeType?.value,
              postWithoutFormatting: true
            } as DataWithNoticeType,
            publisherOrganization
          );
          const isLinerAndDisplayDeadlineSame =
            linerDeadline === displayDeadline;
          const isToday = day.isSame(moment(), 'day');
          return (
            <div
              key={day.toISOString()}
              className={classnames('p-3', {
                'bg-white border b-column-gray-50': isDayEnabled,
                'bg-column-gray-50': !isDayEnabled
              })}
            >
              <h2 className="md:text-right">
                <span
                  className={classnames(
                    'inline-block font-bold p-2 rounded-full',
                    {
                      'bg-column-primary-500 text-white': isToday
                    }
                  )}
                >
                  <span className="md:hidden">{day.format('ddd MMM')}</span>{' '}
                  {day.format('D')}
                </span>
              </h2>
              <div className="mt-2 text-xs">
                {isDayEnabled && isLinerAndDisplayDeadlineSame && (
                  <>
                    <h3 className="font-bold text-column-gray-400">Deadline</h3>
                    <span>{linerDeadline}</span>
                  </>
                )}

                {isDayEnabled && !isLinerAndDisplayDeadlineSame && (
                  <>
                    <h3 className="font-bold text-column-gray-400">Deadline</h3>
                    <div>
                      <ul className="space-y-2">
                        <li>
                          <span className="italic">Liners</span> <br />
                          {linerDeadline}
                        </li>
                        <li>
                          <span className="italic">Displays</span> <br />
                          {displayDeadline}
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {!isDayEnabled && (
                  <span className="block italic text-center">
                    No publication
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getDaysList(startDate: moment.Moment, endDate: moment.Moment) {
  const daysList = [];
  const currentDate = startDate.clone();
  while (currentDate.isBefore(endDate)) {
    daysList.push(currentDate.clone());
    currentDate.add(1, 'day');
  }
  return daysList;
}
